// Claims and deductions against suppliers (Finance → Claims): the reasons, the five ways the amount is worked out, the
// statuses and the settings (Settings → Finance → Claims). Shared by the server (which enforces them) and the screens
// (which show the amount live while typing). Amounts are in the organisation's trading currency.

export const CLAIM_STATUSES = [
  ["draft", "Draft"], ["sent", "Sent to supplier"], ["agreed", "Agreed"], ["disputed", "Disputed"], ["settled", "Settled"], ["cancelled", "Cancelled"],
] as const;
export type ClaimStatus = (typeof CLAIM_STATUSES)[number][0];
export const CLAIM_STATUS_LABEL: Record<string, string> = Object.fromEntries(CLAIM_STATUSES);
/** Still open: not settled and not cancelled. */
export const OPEN_STATUSES: ClaimStatus[] = ["draft", "sent", "agreed", "disputed"];

export const CLAIM_METHODS = [
  ["fixed", "Fixed amount"], ["pct", "% of PO value"], ["piece", "Per piece"], ["late", "Per day late"], ["item", "Item cost + its share of costs"],
] as const;
export type ClaimMethod = (typeof CLAIM_METHODS)[number][0];
export const CLAIM_METHOD_LABEL: Record<string, string> = Object.fromEntries(CLAIM_METHODS);

export const SETTLEMENTS = [
  ["deduct", "Deduct from next payment on this PO"], ["credit", "Supplier credit note"], ["refund", "Supplier refunds the money"],
] as const;
export type Settlement = (typeof SETTLEMENTS)[number][0];
export const SETTLEMENT_LABEL: Record<string, string> = Object.fromEntries(SETTLEMENTS);

/** The six reasons every organisation starts with (they can be hidden, never deleted). */
export const DEFAULT_REASONS: Array<[string, string]> = [
  ["quality", "Quality defect"], ["late", "Late delivery"], ["short", "Short shipment"], ["labels", "Wrong labels or packaging"],
  ["fabric", "Failed fabric test"], ["other", "Other"],
];
export interface ClaimReason { id: string; label: string; builtIn: boolean; hidden: boolean }
export interface ClaimSettings { penaltyPerDay: number; penaltyMax: number; reasons: ClaimReason[] }
export const DEFAULT_CLAIM_SETTINGS: ClaimSettings = {
  penaltyPerDay: 0.5, penaltyMax: 5, reasons: DEFAULT_REASONS.map(([id, label]) => ({ id, label, builtIn: true, hidden: false })),
};

const num = (v: unknown, d: number, min = 0, max = 100) => { const n = Number(v); return Number.isFinite(n) && v !== "" && v !== null ? Math.min(max, Math.max(min, n)) : d; };
const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40);

/** Settings as stored, with the six built-in reasons always present (they can only be hidden). */
export function normalizeClaimSettings(v: unknown): ClaimSettings {
  const x = (v && typeof v === "object" ? v : {}) as any;
  const given = Array.isArray(x.reasons) ? x.reasons : [];
  const reasons: ClaimReason[] = DEFAULT_REASONS.map(([id, label]) => {
    const g = given.find((r: any) => r && r.id === id);
    return { id, label, builtIn: true, hidden: !!g?.hidden };
  });
  for (const r of given) {
    const label = String(r?.label ?? "").trim().slice(0, 80);
    if (!label || DEFAULT_REASONS.some(([id]) => id === r?.id)) continue;
    let id = String(r?.id || "").trim().slice(0, 40) || `own-${slug(label)}`;
    if (!/^own-/.test(id)) id = `own-${slug(id) || slug(label)}`;
    if (reasons.some((y) => y.id === id || y.label.toLowerCase() === label.toLowerCase())) continue;
    reasons.push({ id, label, builtIn: false, hidden: !!r?.hidden });
  }
  return { penaltyPerDay: num(x.penaltyPerDay, 0.5), penaltyMax: num(x.penaltyMax, 5), reasons };
}

/** The costs an "Item cost + its share of costs" claim can include, in order (the groups of Landed cost per item). */
export const ITEM_PARTS = [
  ["fob", "Item cost"], ["freight", "Freight"], ["insurance", "Insurance"], ["duty", "Duty"], ["clearanceOther", "Clearance & other charges"],
] as const;
export type ItemPartKey = (typeof ITEM_PARTS)[number][0];
export const ITEM_PART_LABEL: Record<string, string> = Object.fromEntries(ITEM_PARTS);
/** One cost of the item for all its shipped pieces (filled in by Falcon from Landed cost per item, trading currency). */
export interface ItemPart { key: ItemPartKey; total: number; working: string; source: string }
/** What happens to the agent's commission on the pieces claimed (an internal record, never shown to the supplier). */
export const AGENT_CHOICES = [
  ["waive", "Agent waives it", "Taken off what you owe the agent. Kept as an internal record; the supplier never sees it."],
  ["supplier", "Add it to the supplier's claim", "The supplier pays it back as part of their claim (shown to them as \"Sourcing cost\", without the agent or the %)."],
  ["keep", "Agent keeps it", "Nothing is claimed for commission."],
] as const;
export const AGENT_CHOICE_LABEL: Record<string, string> = Object.fromEntries(AGENT_CHOICES.map(([k, l]) => [k, l]));

/** What a claim's amount is worked out from (only the boxes of its method are used). */
export interface ClaimInputs {
  fixed?: number | null;          // fixed amount
  pct?: number | null;            // % of PO value
  poValue?: number | null;        // the PO value in the trading currency (filled in by Falcon)
  pieces?: number | null;         // pieces affected
  price?: number | null;          // price per piece (defaults to the PO line price)
  daysLate?: number | null;       // days late (filled in from the PO's planned and actual dates when known)
  perDay?: number | null;         // % per day late (default from Settings)
  maxPct?: number | null;         // maximum % (default from Settings)
  // "Item cost + its share of costs" (pieces above is the pieces affected):
  include?: string[];             // the ITEM_PARTS keys ticked
  extra?: number | null;          // your own extra costs (re-labelling, inspection, storage…)
  extraNote?: string;             // what the extra costs are
  agentChoice?: string;           // AGENT_CHOICES key ("" when the PO has no agent commission)
  parts?: ItemPart[];             // filled in by Falcon
  shippedQty?: number | null;     // pieces of the item shipped (filled in by Falcon)
  commission?: number | null;     // agent commission on all shipped pieces (filled in by Falcon)
  commissionPct?: number | null;  // the agent's % (filled in by Falcon)
  shipments?: string[];           // the shipments the item came on (filled in by Falcon)
}
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const pos = (v: unknown) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? 0 : Math.max(0, Number(v)));

/** "Item cost + its share of costs": each cost for the pieces affected (a share of the shipped pieces), the agent's
 *  commission on them, and what is claimed from the supplier. */
export function itemShares(i: ClaimInputs) {
  const shipped = pos(i.shippedQty), pieces = Math.min(Math.floor(pos(i.pieces)), shipped);
  const share = shipped > 0 ? pieces / shipped : 0;
  const include = new Set(i.include || []);
  const parts = (i.parts || []).map((p) => ({ ...p, label: ITEM_PART_LABEL[p.key] || p.key, amount: r2(pos(p.total) * share), included: include.has(p.key) }));
  const commission = r2(pos(i.commission) * share);
  const extra = r2(pos(i.extra));
  const toSupplier = i.agentChoice === "supplier" ? commission : 0;
  const supplier = r2(parts.filter((p) => p.included).reduce((t, p) => t + p.amount, 0) + extra + toSupplier);
  return { pieces, shipped, parts, commission, extra, supplier, agent: i.agentChoice === "waive" ? commission : 0 };
}

/** The claim amount (2 decimals) and, for "per day late", whether the maximum capped it. */
export function claimAmount(method: string, i: ClaimInputs): { amount: number; capped: boolean; pctApplied: number | null } {
  switch (method) {
    case "item": return { amount: itemShares(i).supplier, capped: false, pctApplied: null };
    case "fixed": return { amount: r2(pos(i.fixed)), capped: false, pctApplied: null };
    case "pct": return { amount: r2(pos(i.poValue) * pos(i.pct) / 100), capped: false, pctApplied: pos(i.pct) };
    case "piece": return { amount: r2(Math.floor(pos(i.pieces)) * pos(i.price)), capped: false, pctApplied: null };
    case "late": {
      const raw = Math.floor(pos(i.daysLate)) * pos(i.perDay);
      const max = pos(i.maxPct);
      const p = Math.min(raw, max);
      return { amount: r2(pos(i.poValue) * p / 100), capped: raw > max, pctApplied: r2(p * 1000) / 1000 };
    }
    default: return { amount: 0, capped: false, pctApplied: null };
  }
}

/** A short line saying how the amount was worked out ("120 pieces × 4.50"). */
export function claimWorking(method: string, i: ClaimInputs, cur: string): string {
  const m = (n: number) => `${cur} ${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const r = claimAmount(method, i);
  switch (method) {
    case "fixed": return "Fixed amount";
    case "pct": return `${pos(i.pct)}% of PO value ${m(pos(i.poValue))}`;
    case "piece": return `${Math.floor(pos(i.pieces))} pieces × ${m(pos(i.price))}`;
    case "late": return `${Math.floor(pos(i.daysLate))} days late × ${pos(i.perDay)}% per day${r.capped ? `, capped at ${pos(i.maxPct)}%` : ""} = ${r.pctApplied}% of PO value ${m(pos(i.poValue))}`;
    case "item": {
      const x = itemShares(i);
      const what = [...x.parts.filter((p) => p.included).map((p) => p.label.toLowerCase()), ...(x.extra ? ["your own extra costs"] : []), ...(i.agentChoice === "supplier" && x.commission ? ["sourcing cost"] : [])];
      return `${x.pieces.toLocaleString("en-GB")} of ${x.shipped.toLocaleString("en-GB")} pieces: ${what.join(", ") || "nothing ticked"}`;
    }
    default: return "";
  }
}

/** CL-001, CL-002… (three digits at least). */
export const claimNo = (n: number) => `CL-${String(n).padStart(3, "0")}`;

/** Which boxes must be filled in before a claim can be saved or sent ({ key: message }). */
export function claimProblems(c: { poId?: string | null; reason?: string; method?: string; settlement?: string; inputs?: ClaimInputs; lineId?: string | null }): Record<string, string> {
  const out: Record<string, string> = {};
  if (!c.poId) out.poId = "Choose the purchase order.";
  if (!c.reason) out.reason = "Choose the reason.";
  if (!c.method || !CLAIM_METHOD_LABEL[c.method]) out.method = "Choose how the amount is worked out.";
  if (!c.settlement || !SETTLEMENT_LABEL[c.settlement]) out.settlement = "Choose how it is settled.";
  const i = c.inputs || {};
  {
    if (c.method === "fixed" && !(pos(i.fixed) > 0)) out.fixed = "Enter the amount.";
    if (c.method === "pct" && !(pos(i.pct) > 0)) out.pct = "Enter the %.";
    if (c.method === "piece") { if (!(pos(i.pieces) > 0)) out.pieces = "Enter the pieces affected."; if (!(pos(i.price) > 0)) out.price = "Enter the price per piece."; }
    if (c.method === "late") { if (!(pos(i.daysLate) > 0)) out.daysLate = "Enter the days late."; if (!(pos(i.perDay) > 0)) out.perDay = "Enter the % per day."; if (!(pos(i.maxPct) > 0)) out.maxPct = "Enter the maximum %."; }
    if ((c.method === "pct" || c.method === "late") && !(pos(i.poValue) > 0)) out.poValue = "The PO has no value to work from.";
    if (c.method === "item") {
      if (!c.lineId) out.lineId = "Choose the item on this PO.";
      else if (!(pos(i.shippedQty) > 0)) out.lineId = "This item hasn't been shipped yet, so there are no costs to share. Use \"Per piece\" instead.";
      else if (!(pos(i.pieces) > 0)) out.pieces = "Enter the pieces affected.";
      else if (Math.floor(pos(i.pieces)) > pos(i.shippedQty)) out.pieces = `No more than the ${pos(i.shippedQty)} pieces shipped.`;
      if (!out.lineId && !(i.include || []).length && !(pos(i.extra) > 0)) out.include = "Tick at least one cost.";
      if (!out.lineId && pos(i.commission) > 0 && !AGENT_CHOICE_LABEL[i.agentChoice || ""]) out.agentChoice = "Choose what happens to the agent's commission.";
    }
  }
  if (!out.fixed && !out.pct && !out.pieces && !out.price && !out.daysLate && !out.perDay && !out.maxPct && !out.poValue && !out.lineId && !out.include && !out.agentChoice && c.method && !(claimAmount(c.method, i).amount > 0)) out.amount = "The claim amount must be more than zero.";
  return out;
}
